import { Data, Misc, ModalUtils, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import UpdateCorporateActionEventCommand from '../Models/Commands/UpdateCorporateActionEventCommand';
import CorporateActionEventLookup from '../Models/Lookups/CorporateActionEventLookup';
import CorporateActionEventSearchCriteria from '../Models/Queries/CorporateActionEventSearchCriteria';
import CorporateActionEventTransactionsComponentVM from '../Components/CorporateActionEventTransactionsComponentVM';

export default class CorporateActionEventsVM extends Views.ViewModelBase {
    private corporateActionReferenceValues = new Map<number, string>();
    private isUpdatingCorporateActionReference = false;
    public corporateActionEventSearchCriteria = new CorporateActionEventSearchCriteria();
    public corporateActionReferenceTaskRunner = AppService.get(Types.Neo.TaskRunner);
    public displayCorporateActionEventParent: boolean = true;
    public displayCorporateActionEventTransactionsComponent: boolean = false;
    public corporateActionEventTransactionsComponentVM = new CorporateActionEventTransactionsComponentVM();

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        public corporateActionEventsApiClient = AppService.get(Types.ApiClients.CorporateActionEventsApiClient),
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
        this.makeObservable();
    }

    public pageManager = new Data.PageManager(this.corporateActionEventSearchCriteria, CorporateActionEventLookup, this.corporateActionEventsApiClient.getCorporateActionEventsPagedList, {
        pageSize: 10,
        sortAscending: false,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    });

    public async initialise() {
        reaction(() => this.corporateActionEventSearchCriteria.rawEventType, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.corporateActionEventSearchCriteria.rawCorporateActionType, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.corporateActionEventSearchCriteria.rawUnderlyingSecurityCode, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 650 });
        reaction(() => this.corporateActionEventSearchCriteria.rangeStartDate, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 0 });
        reaction(() => this.corporateActionEventSearchCriteria.rangeEndDate, () => this.pageManager.refreshData(), { fireImmediately: false, delay: 0 });
    }

    public rememberCorporateActionReference(corporateActionEvent: CorporateActionEventLookup) {
        this.corporateActionReferenceValues.set(corporateActionEvent.corporateActionEventID, corporateActionEvent.corporateActionReference ?? "");
    }

    public async updateCorporateActionReference(corporateActionEvent: CorporateActionEventLookup) {
        if (this.isUpdatingCorporateActionReference) {
            return;
        }

        this.isUpdatingCorporateActionReference = true;

        try {
            const corporateActionEventID = corporateActionEvent.corporateActionEventID;
            const currentValue = corporateActionEvent.corporateActionReference ?? "";
            const originalValue = this.corporateActionReferenceValues.get(corporateActionEventID);

            await this.setCorporateActionEventTransactionsSearchCriteria(corporateActionEvent);
            const totalRecords = this.corporateActionEventTransactionsComponentVM.pageManager.totalRecords;

            if (await ModalUtils.showYesNo(TextConstants.Titles.ConfirmCorporateActionReferenceUpdate,
                `This change will update ${totalRecords} transaction records which are associated with the corporate action. Do you want to continue?`) === Misc.ModalResult.Yes) {
                if (originalValue === undefined) {
                    this.corporateActionReferenceValues.set(corporateActionEventID, currentValue);
                    return;
                }

                if (originalValue === currentValue) {
                    return;
                }

                await this.corporateActionReferenceTaskRunner.run(async () => {
                    const command = this.mapCorporateActionEventToUpdateCommand(corporateActionEvent);
                    const response = await this.corporateActionEventsApiClient.updateCorporateActionEvent(command.toJSObject());

                    if (response.data.success) {
                        this.corporateActionReferenceValues.set(corporateActionEventID, currentValue);
                        NotifyUtils.addSuccess(TextConstants.Titles.Success, TextConstants.Notifications.CorporateActionEventSaved, NotificationDuration.Standard);
                    } else {
                        corporateActionEvent.corporateActionReference = originalValue;
                        this.corporateActionReferenceValues.set(corporateActionEventID, originalValue);
                        NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                    }
                });
            }
            else {
                this.corporateActionReferenceValues.set(corporateActionEventID, originalValue ?? "");
                corporateActionEvent.corporateActionReference = originalValue ?? "";
            }
        } finally {
            this.isUpdatingCorporateActionReference = false;
        }
    }

    private mapCorporateActionEventToUpdateCommand(corporateActionEvent: CorporateActionEventLookup) {
        const command = new UpdateCorporateActionEventCommand();
        command.corporateActionEventID = corporateActionEvent.corporateActionEventID;
        command.corporateActionReference = corporateActionEvent.corporateActionReference ?? "";

        return command;
    }

    private async setCorporateActionEventTransactionsSearchCriteria(corporateActionEvent: CorporateActionEventLookup) {
        this.corporateActionEventTransactionsComponentVM.corporateActionEventTransactionsSearchCriteria.corporateActionEventId = corporateActionEvent.corporateActionEventID;
        this.corporateActionEventTransactionsComponentVM.corporateActionEventTransactionsSearchCriteria.staticServiceProviderFileGroupingId = corporateActionEvent.staticServiceProviderFileGroupingID;
        this.corporateActionEventTransactionsComponentVM.corporateActionEventTransactionsSearchCriteria.instrument = corporateActionEvent.instrument;
        await this.corporateActionEventTransactionsComponentVM.pageManager.refreshData();
    }

    public showCorporateActionEventTransactionsComponent(corporateActionEvent: CorporateActionEventLookup) {
        this.setCorporateActionEventTransactionsSearchCriteria(corporateActionEvent);
        this.corporateActionEventTransactionsComponentVM.selectedCorporateAction = corporateActionEvent;

        if (corporateActionEvent.instrument == "SECURITY") {
            this.corporateActionEventTransactionsComponentVM.showSecurityTransactions = true;
            this.corporateActionEventTransactionsComponentVM.showCashTransactions = false;
        } else if (corporateActionEvent.instrument == "CASH") {
            this.corporateActionEventTransactionsComponentVM.showSecurityTransactions = false;
            this.corporateActionEventTransactionsComponentVM.showCashTransactions = true;
        }
        this.displayCorporateActionEventTransactionsComponent = true;
        this.displayCorporateActionEventParent = false;
    }

    public backToCorporateActionEvents() {
        this.displayCorporateActionEventTransactionsComponent = false;
        this.displayCorporateActionEventParent = true;
    }
}